import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Widens the sm+ panel for content that needs the extra room, such as a
   * form paired with a live preview. The panel still docks to the right
   * edge and stays full height; omitting the prop keeps every existing
   * caller's width exactly as it was.
   */
  wide?: boolean;
}

/**
 * Reusable slide-over (desktop, right edge) / bottom sheet (mobile) shell.
 * Matches the app's modal chrome (white panel, rounded-lg, shadow-xl, sticky
 * header + X, blast-600 actions). Dismiss via X / backdrop / Escape.
 * Swipe-down dismissal is intentionally deferred for v1.
 */
export const SlideOver = ({ isOpen, onClose, title, children, footer, wide = false }: SlideOverProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        data-testid="slideover-backdrop"
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Panel: bottom sheet on mobile, right drawer on sm+.
          sm:top-0 + sm:bottom-0 are spelled out individually (not
          sm:inset-y-0) because inset-y-0 and bottom-auto both set the
          `bottom` CSS property — with two utilities writing the same
          property, Tailwind's generated source order (not class order)
          decides the winner, and bottom-auto used to win, leaving the
          panel's height unconstrained/content-driven instead of pinned
          to the viewport. */}
      <div
        className={`fixed bg-white dark:bg-gray-800 shadow-xl flex flex-col
          inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl
          sm:top-0 sm:bottom-0 sm:right-0 sm:left-auto sm:max-h-none sm:rounded-none sm:rounded-l-lg
          ${wide ? 'sm:w-[90vw] sm:max-w-4xl lg:max-w-5xl' : 'sm:w-[28rem]'}`}
        role="dialog"
        aria-modal="true"
      >
        {/* Grab handle (mobile only) */}
        <div className="sm:hidden mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />

        {/* Sticky header — matches AddActivityModal chrome */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 min-w-0">{title}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body — min-h-0 overrides the flex item's default auto min-height,
            which otherwise lets this grow to fit its content instead of
            capping at the flex-basis and scrolling internally. */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">{children}</div>

        {/* Footer — matches AddActivityModal footer chrome */}
        {footer && (
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
